import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';

// Selector con búsqueda para catálogos grandes (1400+ centros). Reemplaza un
// <Select> que renderizaba TODOS los <SelectItem> del catálogo por cada fila
// de la tabla — con 1457 centros x 129 NM sin sede eso son ~188.000 nodos y
// colgaba/crasheaba el navegador al abrir el desplegable (P0 03/09).
// Sólo se renderizan como máximo MAX_RESULTS coincidencias a la vez.
const MAX_RESULTS = 50;

export function CentroCombobox({ centros = [], value, onSelect, placeholder = 'Selecciona sede…', testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = useMemo(() => centros.find(c => c.codigo === value), [centros, value]);

  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? centros.filter(c => c.nombre?.toLowerCase().includes(q) || c.codigo?.toLowerCase().includes(q))
      : centros;
    return list.slice(0, MAX_RESULTS);
  }, [centros, search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="h-7 text-xs w-full flex items-center justify-between rounded-md border px-2 bg-transparent"
          style={{ borderColor: 'var(--vz-border-subtle)', color: selected ? 'var(--vz-text-primary)' : 'var(--vz-text-muted)' }}
        >
          <span className="truncate">{selected ? `${selected.nombre} · ${selected.codigo}` : placeholder}</span>
          <ChevronsUpDown size={12} className="ml-1 flex-shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Buscar por nombre o código…" value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty className="text-xs py-3">Sin resultados</CommandEmpty>
            <CommandGroup>
              {results.map(c => (
                <CommandItem
                  key={c.codigo}
                  value={c.codigo}
                  onSelect={() => { onSelect(c); setOpen(false); setSearch(''); }}
                  className="text-xs"
                  data-testid={`${testId}-option-${c.codigo}`}
                >
                  <Check size={12} className={cn('mr-1.5', value === c.codigo ? 'opacity-100' : 'opacity-0')} />
                  {c.nombre} · {c.codigo}
                </CommandItem>
              ))}
            </CommandGroup>
            {centros.length > MAX_RESULTS && results.length === MAX_RESULTS && (
              <p className="text-[10px] text-center py-1" style={{ color: 'var(--vz-text-muted)' }}>
                Mostrando {MAX_RESULTS} de {centros.length} — refina la búsqueda
              </p>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
